import TYPES                         from "@/core/services/Changelogs/types";
import SerializeSimplified           from "@/core/misc/decorators/SerializeSimplified";
import Title_ru_RU                   from "@/core/misc/decorators/Title_ru_RU";

import Core                          from "@/core/Core";
import AbstractDocument              from '@/domains/Abstract/AbstractDocument';
import CargoUnit                     from "@/domains/CargoUnits/CargoUnit";
import Person                        from '@/domains/Persons/Person';
import Store                         from '@/domains/Stores/Store';
import IntRange                      from "@/core/types/IntRange";
import Dictionary                    from "@/core/types/Dictionary";
import DeliveryType                  from "@/domains/DeliveryTypes/DeliveryType";
import Weight                        from '@/core/types/Weight';
import Volume                        from '@/core/types/Volume';
import Qty                           from '@/core/types/Qty';

import type OrderPosition            from "@/domains/Orders/OrderPosition";
import type AbstractDocumentPosition from '@/domains/Abstract/AbstractDocumentPosition';

/**
 * Абстрактный транспортный документ (отгрузка, сборка, ЗТК)
 */
abstract class AbstractShipmentDocument<TPosition = any> extends AbstractDocument<TPosition> {

  @Title_ru_RU('Тип доставки', TYPES.OBJECT)
  @SerializeSimplified
  public deliveryType: DeliveryType;

  @Title_ru_RU('Пункт отправки', TYPES.OBJECT)
  @SerializeSimplified
  public sellerStore: Store;

  @Title_ru_RU('пункт доставки', TYPES.OBJECT)
  @SerializeSimplified
  public buyerStore: Store;

  /** Ответственный от поставщика */
  @SerializeSimplified
  private responsibleSeller: Person | null;

  // ---- ВГХ ----
  @Title_ru_RU('Объём', TYPES.NUMBER)
  public cargoVolume: Volume = Volume.zero();

  @Title_ru_RU('Вес брутто', TYPES.NUMBER)
  public cargoWeightGross: Weight = Weight.zero();

  @Title_ru_RU('Вес нетто', TYPES.NUMBER)
  public cargoWeightNet: Weight = Weight.zero();

  @Title_ru_RU('Количество паллетомест', TYPES.NUMBER)
  public cargoUnitsCount: Qty = Qty.zero();

  /** Отгрузочные места */
  public cargoUnits: Dictionary<number | string, CargoUnit> = new Dictionary().useKeyExtractor(unit => unit.compositeKey);

  @Title_ru_RU('Температурный режим', TYPES.INT_RANGE)
  public storeTemperatureRange: IntRange;

  static fromServer(data: any, result: AbstractShipmentDocument): AbstractShipmentDocument {
    super.fromServer(data, result);

    result.deliveryType = Core.utils.hydrate(data.deliveryType, DeliveryType.fromServer)!;

    result.sellerStore = Core.utils.hydrate(data.sellerStore, Store.fromServer)!;
    result.buyerStore = Core.utils.hydrate(data.buyerStore, Store.fromServer)!;

    result.responsibleSeller = Core.utils.hydrate(data.responsibleSeller, Person.fromServer)!;

    /** Скаляры разливаются по именам сами, но Numeric-типы автомат не
     * подхватывает — их всегда гидрируем принудительно через хелпер. */
    result.cargoVolume      = Core.utils.hydrate(data.cargoVolume, Volume.fromServer) ?? Volume.zero();
    result.cargoUnitsCount  = Core.utils.hydrate(data.cargoUnitsCount, Qty.fromServer) ?? Qty.zero();
    result.cargoWeightGross = Core.utils.hydrate(data.cargoWeightGross, Weight.fromServer) ?? Weight.zero();
    result.cargoWeightNet   = Core.utils.hydrate(data.cargoWeightNet, Weight.fromServer) ?? Weight.zero();

    const cargoUnitsMap = new Dictionary<string, CargoUnit>().useKeyExtractor(unit => unit.compositeKey);
    cargoUnitsMap.add(Core.utils.hydrateArray(data.cargoUnits, CargoUnit.fromServer));

    result.cargoUnits = cargoUnitsMap;

    result.storeTemperatureRange = Core.utils.hydrate(data.storeTemperatureRange, IntRange.fromServer)!;

    return result;
  }

  static fromOrderPosition(orderPosition: OrderPosition, result: AbstractShipmentDocument): AbstractShipmentDocument {
    super.fromOrderPosition(orderPosition, result);

    result.deliveryType = orderPosition.deliveryType;

    result.sellerStore = orderPosition.sellerStore;
    result.buyerStore = orderPosition.buyerStore;

    result.storeTemperatureRange = orderPosition.storeTemperatureRange;

    return result;
  }

  static fromNew(result: AbstractShipmentDocument): AbstractShipmentDocument {
    super.fromNew(result);

    if (Core.currentPartner?.isSeller) {
      result.responsibleSeller = Core.currentUser;
    }
    else if (Core.currentPartner?.isBuyer) {
      /** 47 = "633004, Новосибирская обл., г. Бердск ...", 564 = "Новосибирск" */
      if (result.buyer?.isLSZ) {
        result.buyerStore = result.buyer.stores.find(_store => _store.id === 47) || result.buyer.stores[0];
      }
      else if (result.buyer?.stores.length === 1) {
        result.buyerStore = result.buyer.stores[0];
      }
    }

    result.storeTemperatureRange = new IntRange();

    return result;
  }


  get sellerStoreString(): string {
    if (!this.sellerStore) {
      return 'Не выбран';
    }
    if (this.sellerStore.name) {
      return this.sellerStore.name;
    }
    return this.sellerStore.address.stringRepresentation;
  }

  get buyerStoreString(): string {
    if (!this.buyerStore) {
      return 'Не выбран';
    }
    if (this.buyerStore.name) {
      return this.buyerStore.name;
    }
    return this.buyerStore.address.stringRepresentation;
  }

  /**
   * Ответственный за документ зависит от роли текущего партнёра:
   * продавец редактирует своего ответственного, покупатель — своего.
   */
  get responsible(): Person {
    if (Core.currentPartner?.isSeller) {
      return this.responsibleSeller;
    }
    else {
      return this.responsibleBuyer;
    }
  }

  set responsible(value: Person) {
    if (Core.currentPartner?.isSeller) {
      this.responsibleSeller = value;
    }
    else {
      this.responsibleBuyer = value;
    }
  }

  addPosition(newPosition: TPosition): void {
    this.validatePosition(newPosition);

    super.addPosition(newPosition);
  }

  /**
   * Проверяет, что позиция совместима с документом: возить позицию с одним
   * типом доставки в документе с другим нельзя.
   *
   * Позиции всех транспортных документов наследуют AbstractDocumentPosition,
   * поэтому ссылка на позицию заказа у них есть всегда.
   */
  validatePosition(position: TPosition): void {
    const orderPosition = (position as AbstractDocumentPosition).orderPosition;

    if (this.deliveryType.id !== orderPosition.deliveryType?.id) {
      throw Error('Не совпадает тип доставки у позиции и документа');
    }

    super.validatePosition(position);
  }


  addCargoUnit(cargoUnit: CargoUnit): void {
    if (!CargoUnit.isCargoUnit(cargoUnit)) {
      console.error('addCargoUnit: аргумент не является экземпляром CargoUnit');
      return;
    }
    this.cargoUnits.add(cargoUnit);
    this.calculateCargoSummary();
  }

  removeCargoUnit(cargoUnit: CargoUnit): void {
    this.cargoUnits.delete(cargoUnit.compositeKey);
    this.calculateCargoSummary();
  }

  updateCargoUnit(updatedCargoUnit: CargoUnit, originalCompositeKey: string): void {
    if (!CargoUnit.isCargoUnit(updatedCargoUnit)) {
      console.error('updateCargoUnit: аргумент не является экземпляром CargoUnit');
      return;
    }

    if (originalCompositeKey !== updatedCargoUnit.compositeKey) {
      this.cargoUnits.delete(originalCompositeKey);
    }

    this.cargoUnits.set(updatedCargoUnit.compositeKey, updatedCargoUnit);
    this.calculateCargoSummary();
  }

  calculateCargoSummary(): void {
    /** Агрегаты по местам через типовой plus со старта zero(). round10 не нужен —
     * каждый тип нормализован до своего scale конструктором (Volume — вверх). */
    const qtyTotal    = this.cargoUnits.reduce((sum, item) => sum.plus(item.qty), Qty.zero());
    const grossTotal  = this.cargoUnits.reduce((sum, item) => sum.plus(item.totalWeightGross), Weight.zero());
    const netTotal    = this.cargoUnits.reduce((sum, item) => sum.plus(item.totalWeightNet), Weight.zero());
    const volumeTotal = this.cargoUnits.reduce((sum, item) => sum.plus(item.totalVolume), Volume.zero());

    this.cargoUnitsCount  = qtyTotal;
    this.cargoWeightGross = grossTotal;
    this.cargoWeightNet   = netTotal;
    this.cargoVolume      = volumeTotal;
  }

  get storeTemperatureRangeString(): string {
    return this.storeTemperatureRange?.toString({unit: '°C', negative: 'Нет'}) ?? 'Нет';
  }
}

export default AbstractShipmentDocument;
